import type { User, PatchUserDto, SearchUser } from "@repo/exit-calc-interfaces";
import { z } from "zod";
import type { HttpClient } from "../http";
import type { DataAndMessageResponse, MessageResponse } from "../types";

const UserSchema: z.ZodType<User> = z.object({
  id: z.number(),
  email: z.string(),
  nickname: z.string().optional().nullable(),
  avatarUrl: z.string().optional().nullable(),
  isProfileComplete: z.boolean(),
  createdAt: z.coerce.date(),
});

const SearchUserSchema: z.ZodType<SearchUser> = z.object({
  id: z.number(),
  email: z.string(),
  nickname: z.string().nullish(),
  avatarUrl: z.string().nullish(),
});

const GetUserResponseSchema: z.ZodType<DataAndMessageResponse<User>> = z.object(
  {
    data: UserSchema,
    message: z.string(),
  },
);

const PatchUserResponseSchema: z.ZodType<DataAndMessageResponse<User>> =
  z.object({
    data: UserSchema,
    message: z.string(),
  });

const DeleteUserResponseSchema: z.ZodType<MessageResponse> = z.object({
  message: z.string(),
});

const SearchUserResponseSchema: z.ZodType<
  DataAndMessageResponse<SearchUser[]>
> = z.object({
  data: z.array(SearchUserSchema),
  message: z.string(),
});

export function createUserModule(httpClient: HttpClient) {
  return {
    logout: async () => {
      await httpClient.post("/api/v1/auth/logout");
    },

    get: async () => {
      const response = await httpClient.get<typeof GetUserResponseSchema>(
        "/api/v1/user",
        {
          schema: GetUserResponseSchema,
        },
      );
      return response.data;
    },

    patch: async (data: PatchUserDto) => {
      const response = await httpClient.patch<typeof PatchUserResponseSchema>(
        "/api/v1/user",
        {
          data,
          schema: PatchUserResponseSchema,
        },
      );
      return response.data;
    },

    deleteAccount: async () => {
      const response = await httpClient.delete<typeof DeleteUserResponseSchema>(
        "/api/v1/user",
        {
          schema: DeleteUserResponseSchema,
        },
      );
      return response;
    },

    search: async (query: string) => {
      const response = await httpClient.get<typeof SearchUserResponseSchema>(
        `/api/v1/users/search?q=${encodeURIComponent(query)}`,
        {
          schema: SearchUserResponseSchema,
        },
      );
      return response.data;
    },
  };
}
